import axios from "axios";
import logoDefault from "../assets/hyppe-logo.jpeg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Normalize an axios/API error into a human-readable string. Critically handles
// FastAPI 422 responses where `detail` is an ARRAY of Pydantic error objects —
// rendering that array directly in React/toast throws "Objects are not valid as a React child".
export const formatApiError = (err, fallback = "Something went wrong. Please retry.") => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msg = detail.map((d) => (d && d.msg) || (typeof d === "string" ? d : "")).filter(Boolean).join("; ");
    return msg || fallback;
  }
  if (typeof detail === "object") return detail.msg || fallback;
  return String(detail);
};

// Build a full URL for a stored file path (e.g. "/api/files/...")
export const fileUrl = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${BACKEND_URL}${p}`;
};

// INR formatter — preserves entered decimals (no rounding to whole rupees).
// Whole numbers show no decimals (₹150); fractional values show 2 (₹149.99, ₹199.50).
export const fmt = (rupees) => {
  const n = Number(rupees) || 0;
  const hasDecimals = Math.round(n * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR",
    minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2,
  }).format(n);
};
// Backwards-compatible alias used by existing pages
export const fmtPrice = fmt;

export const padId = (n) => String(n).padStart(2, "0");

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// ===== V2.0 dynamic catalog helpers =====
// The admin-entered Product Name is always authoritative for display.
// "Inspired by …" is only a fallback used when no name is set.
export const displayName = (p) =>
  (p?.name || "").trim() || ((!p?.is_traditional && p?.inspired_by) ? `Inspired by ${p.inspired_by}` : "");

// Global variant price = size.base_price * concentration.multiplier, unless a per-product override exists.
// Kept to 2-decimal precision (no rounding to whole rupees) so entered prices display exactly.
export const priceFor = (product, size, conc) => {
  if (!size || !conc) return 0;
  const k = `${size.key}::${conc.key}`;
  const o = (product?.price_overrides || {})[k];
  if (o != null && o !== "") return round2(Number(o));
  return round2((size.base_price || 0) * (conc.multiplier || 1));
};

export const concsForProduct = (product, allConcs) =>
  (allConcs || []).filter((c) => (product?.types || []).includes(c.product_type));

export const sizesForType = (allSizes, productType) =>
  (allSizes || []).filter((s) => s.product_type === productType);

// Lowest achievable price across a product's available variants (for "from ₹X").
export const minPriceForProduct = (product, sizes, concs) => {
  const cs = concsForProduct(product, concs);
  let min = Infinity;
  cs.forEach((c) => {
    sizesForType(sizes, c.product_type).forEach((s) => {
      const p = priceFor(product, s, c);
      if (p > 0 && p < min) min = p;
    });
  });
  return min === Infinity ? 0 : min;
};

// Brand logo bundled into the build (hashed asset) so it always resolves in every
// environment (dev preview + production Docker) regardless of DB/public-path state.
export const LOGO_URL = logoDefault;

// Family → curated hero image
export const FAMILY_IMAGES = {
  fresh:    "https://images.unsplash.com/photo-1547887537-6158d64c35b3?auto=format&fit=crop&w=900&q=80",
  woody:    "https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?auto=format&fit=crop&w=900&q=80",
  floral:   "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=900&q=80",
  oriental: "https://images.unsplash.com/photo-1615634260167-c8cdede054de?auto=format&fit=crop&w=900&q=80",
  oud:      "https://images.unsplash.com/photo-1588405748880-12d1d2a59d75?auto=format&fit=crop&w=900&q=80",
  attar:    "https://images.pexels.com/photos/30981935/pexels-photo-30981935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
  fruity:   "https://images.unsplash.com/photo-1592914610354-fd354ea45e48?auto=format&fit=crop&w=900&q=80",
};

export const FAMILY_LABELS = {
  fresh: "Fresh & Citrus",
  woody: "Woody & Aromatic",
  floral: "Floral",
  oriental: "Oriental Amber",
  oud: "Oud",
  attar: "Attar",
  fruity: "Fruity Chypre",
};

// ===== Dynamic family/category helpers (admin-editable via catalog) =====
// Resolve the display label: catalog family → product's own family_label → static fallback → raw key.
export const familyLabelFor = (product, families) => {
  const f = (families || []).find((x) => x.key === product?.family);
  return f?.label || product?.family_label || FAMILY_LABELS[product?.family] || product?.family || "";
};

// Resolve the card image: product's own image → catalog family image → static fallback.
export const familyImageFor = (product, families) => {
  if (product?.images && product.images[0]) return product.images[0];
  const f = (families || []).find((x) => x.key === product?.family);
  return f?.image_url || FAMILY_IMAGES[product?.family] || FAMILY_IMAGES.floral;
};

// Resolve the ordered gallery to display for a product at a given bottle-size key.
// Fallback chain: size gallery → main gallery → legacy images[] → family image.
// Always returns a non-empty list of { url, alt } (never empty / broken).
export const galleryFor = (product, sizeKey, families) => {
  const g = (product && product.galleries) || {};
  const norm = (arr) =>
    (arr || [])
      .map((x) => (typeof x === "string" ? { url: x, alt: "" } : x))
      .filter((x) => x && x.url);
  let imgs = sizeKey ? norm(g[sizeKey]) : [];
  if (!imgs.length) imgs = norm(g.main);
  if (!imgs.length && product?.images?.length) imgs = norm(product.images);
  if (!imgs.length) imgs = [{ url: familyImageFor(product, families), alt: product?.name || "" }];
  return imgs;
};

// Resolve a label directly from a family key (used by the shop filter chips).
export const familyLabelByKey = (key, families) => {
  const f = (families || []).find((x) => x.key === key);
  return f?.label || FAMILY_LABELS[key] || key;
};
